/**
 * Mộc By Dylan — Inventory Dashboard
 * ────────────────────────────────────
 * Handles loading, rendering, searching, and saving inventory.
 * Waits for 'inv:auth-success' from inventory-auth.js before running.
 *
 * APIs consumed:
 *   GET  /api/internal/inventory              → { items: [...] }
 *   PATCH /api/internal/inventory/:itemId     → body: { quantity: n }
 *
 * All requests include header: X-Staff-Email (from localStorage via InventoryAuth)
 */

'use strict';

/* ── API config ────────────────────────────────────────────── */
const API = {
  base: 'https://mocbydylan-payment-production.up.railway.app/api/internal',

  /** Build headers for every request. Includes X-Staff-Email automatically. */
  headers() {
    const auth  = /** @type {any} */ (window).InventoryAuth;
    const email = auth?.getStoredEmail() ?? '';
    return {
      'Content-Type': 'application/json',
      ...(email ? { 'X-Staff-Email': email } : {}),
    };
  },
};

/* ── Collection filter definitions ────────────────────────── */

/**
 * Fixed collection list in display order.
 * `value` must match the Shopify collection handle stored in item.collections[].handle.
 *
 * NOTE: Mộc Figure uses handle "moc-figures" (plural) in Shopify.
 * NOTE: collectionType from the API is unreliable (always "Other") — do not use it.
 */
const COLLECTIONS = [
  { value: 'all',          label: 'All'        },
  { value: 'moc-wear',     label: 'Mộc Wear'   },
  { value: 'moc-care',     label: 'Mộc Care'   },
  { value: 'moc-figures',  label: 'Mộc Figure' },
  { value: 'moc-charm',    label: 'Mộc Charm'  },
  { value: 'moc-living',   label: 'Mộc Living' },
];

/**
 * Return true if `item` should be shown for the given collection handle.
 * Looks at item.collections[].handle (the Shopify collections array on each item).
 * 'all' always returns true.
 *
 * @param {any}    item    Inventory item from the API
 * @param {string} handle  'all' | 'moc-wear' | 'moc-care' | 'moc-figures' | …
 * @returns {boolean}
 */
function itemMatchesCollection(item, handle) {
  if (handle === 'all') return true;
  const cols = item.collections;
  if (!Array.isArray(cols)) return false;
  return cols.some((c) => c.handle === handle);
}

/* ── In-memory state ───────────────────────────────────────── */

/** @type {Array<object>} Full item list returned by API */
let allItems = [];

/** @type {Array<object>} Currently displayed items (after all filters) */
let visibleItems = [];

/**
 * Currently active collection handle ('all' = show everything).
 * Must match item.collections[].handle exactly, or 'all'.
 */
let activeCollection = 'all';

/** Current search query string */
let searchQuery = '';


/* ── API calls ─────────────────────────────────────────────── */

/**
 * Fetch all inventory items.
 * @returns {Promise<Array<object>>}
 */
async function loadInventory() {
  const res = await fetch(`${API.base}/inventory`, {
    headers: API.headers(),
  });
  if (!res.ok) throw new Error(`GET /inventory failed: ${res.status}`);
  const data = await res.json();
  return data.items ?? [];
}

/* ── Item ID helper ────────────────────────────────────────── */

/**
 * Resolve the inventory item ID from an item object.
 * Handles both camelCase and snake_case field names.
 * @param {object} item
 * @returns {string|number}
 */
function itemId(item) {
  return item.inventoryItemId ?? item.inventory_item_id ?? item.id;
}

/* ── Client-side search ────────────────────────────────────── */

/**
 * Resolve the display variant string for an item.
 * Priority: displayVariant → variantTitle → variant_title
 * Returns '' if the resolved value is "Default Title" or empty.
 * @param {object} item
 * @returns {string}
 */
function resolveVariant(item) {
  const raw = item.displayVariant ?? item.variantTitle ?? item.variant_title ?? '';
  return raw.toLowerCase() === 'default title' ? '' : raw;
}

/**
 * Filter items by product title, display variant, or SKU (case-insensitive).
 * @param {string} query
 * @param {Array<object>} items
 * @returns {Array<object>}
 */
function searchInventory(query, items) {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((item) => {
    const variant = resolveVariant(item);
    return (
      (item.title ?? '').toLowerCase().includes(q) ||
      variant.toLowerCase().includes(q)            ||
      (item.sku   ?? '').toLowerCase().includes(q)
    );
  });
}

/* ── Rendering ─────────────────────────────────────────────── */

/**
 * Render the product grid from an array of items.
 * @param {Array<object>} items
 */
function renderGrid(items) {
  const grid = document.getElementById('inv-grid');
  if (!grid) return;

  if (!items.length) {
    grid.innerHTML = '';
    return;
  }

  grid.innerHTML = items.map((item) => renderCard(item)).join('');
}

/**
 * Format a price decimal string (e.g. "140000.00") as Vietnamese Dong.
 * Returns '' when the value is missing or unparseable.
 * @param {string|number|undefined} priceStr
 * @returns {string}
 */
function formatPrice(priceStr) {
  if (priceStr == null || priceStr === '') return '';
  const num = parseFloat(priceStr);
  if (isNaN(num)) return '';
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(num);
}

/**
 * Build the HTML string for a single product card (read-only display).
 *
 * API field priority:
 *   variant  → displayVariant > variantTitle > variant_title  (hidden if "Default Title")
 *   quantity → quantity > stock
 *   price    → item.price (decimal string from Shopify, formatted as VND)
 *
 * @param {object} item
 * @returns {string}
 */
function renderCard(item) {
  const qty     = item.quantity ?? item.stock ?? 0;
  const variant = resolveVariant(item);
  const sku     = item.sku ?? '';
  const price   = formatPrice(item.price);

  const imgHtml = item.image
    ? `<img class="inv-card__img" src="${esc(item.image)}" alt="${esc(item.title)}" width="110" height="110" loading="lazy">`
    : `<div class="inv-card__img-placeholder" aria-hidden="true">
         <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
           <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" stroke-width="1.2"/>
           <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" stroke-width="1.2"/>
           <path d="M3 15l5-4 4 4 3-3 6 5" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>
         </svg>
       </div>`;

  const variantHtml = variant
    ? `<div class="inv-card__variant-wrap">
         <span class="inv-card__variant-chip">${esc(variant)}</span>
       </div>`
    : '';

  const skuHtml = sku
    ? `<p class="inv-card__sku"><span class="inv-card__meta-label">SKU</span>${esc(sku)}</p>`
    : '';

  const priceHtml = price
    ? `<p class="inv-card__price"><span class="inv-card__meta-label">GIÁ</span><strong>${esc(price)}</strong></p>`
    : '';

  return `
    <div class="inv-card">
      <div class="inv-card__inner">
        <div class="inv-card__img-wrap">${imgHtml}</div>
        <div class="inv-card__body">
          <p class="inv-card__title">${esc(item.title ?? '—')}</p>
          ${variantHtml}
          ${skuHtml}
          ${priceHtml}
          <p class="inv-card__qty"><span class="inv-card__meta-label">QTY</span><strong>${qty}</strong></p>
        </div>
      </div>
    </div>`;
}

/* ── State helpers ─────────────────────────────────────────── */

/**
 * Show/hide the loading skeleton.
 * @param {boolean} show
 */
function setLoading(show) {
  const loading = document.getElementById('inv-loading');
  const grid    = document.getElementById('inv-grid');
  if (loading) loading.hidden = !show;
  if (grid)    grid.hidden = show;
}

/**
 * Show/hide the empty-state message.
 * @param {boolean} show
 */
function setEmpty(show) {
  const el = document.getElementById('inv-empty');
  if (el) el.hidden = !show;
}

/**
 * Show/hide the error state.
 * @param {boolean} show
 */
function setError(show) {
  const el = document.getElementById('inv-error');
  if (el) el.hidden = !show;
}

/**
 * Update the item count label. Always shows "N product(s)".
 * @param {number} count
 */
function updateCount(count) {
  const el = document.getElementById('inv-count');
  if (!el) return;
  el.textContent = `${count} ${count === 1 ? 'product' : 'products'}`;
}

/* ── Toast ─────────────────────────────────────────────────── */

/**
 * Show a dismissable toast notification.
 * @param {string} message
 * @param {'success'|'error'} [type='success']
 * @param {number} [duration=3500]
 */
function showToast(message, type = 'success', duration = 3500) {
  const region = document.getElementById('inv-toasts');
  if (!region) return;

  const icon = type === 'success'
    ? `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" style="flex-shrink:0">
         <circle cx="8" cy="8" r="7" stroke="#5B8C5C" stroke-width="1.25"/>
         <path d="M5 8l2 2 4-4" stroke="#5B8C5C" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
       </svg>`
    : `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" style="flex-shrink:0">
         <circle cx="8" cy="8" r="7" stroke="#C05454" stroke-width="1.25"/>
         <path d="M8 5v4M8 11v.5" stroke="#C05454" stroke-width="1.25" stroke-linecap="round"/>
       </svg>`;

  const toast = document.createElement('div');
  toast.className = `inv-toast inv-toast--${type}`;
  toast.setAttribute('role', 'status');
  toast.innerHTML = `${icon}<span>${esc(message)}</span>`;
  region.appendChild(toast);

  const dismiss = () => {
    toast.classList.add('inv-toast--out');
    toast.addEventListener('animationend', () => toast.remove(), { once: true });
  };

  const t = setTimeout(dismiss, duration);
  toast.addEventListener('click', () => { clearTimeout(t); dismiss(); });
}


/* ── Event handling ────────────────────────────────────────── */

function bindEvents() {
  const root = document.getElementById('inv-root');
  if (!root) return;

  /* Collection filter chips */
  root.addEventListener('click', (e) => {
    const chip = e.target.closest('.inv-filter-chip');
    if (!chip) return;
    const handle = chip.dataset.collection;
    if (!handle || handle === activeCollection) return;

    activeCollection = handle;

    /* Update active chip visual */
    root.querySelectorAll('.inv-filter-chip').forEach((c) => {
      const btn = /** @type {HTMLButtonElement} */ (c);
      c.classList.toggle('inv-filter-chip--active', btn.dataset.collection === handle);
      c.setAttribute('aria-pressed', btn.dataset.collection === handle ? 'true' : 'false');
    });

    applyFilters();
  });

  /* Clear search */
  root.addEventListener('click', (e) => {
    if (e.target.closest('#inv-clear-search')) {
      const input = document.getElementById('inv-search');
      if (input) {
        input.value = '';
        searchQuery = '';
        applyFilters();
      }
    }
  });

  /* Retry */
  root.addEventListener('click', (e) => {
    if (e.target.closest('#inv-retry')) init();
  });

  /* Search — debounced, persists across collection changes */
  const searchEl = document.getElementById('inv-search');
  let debounce = null;
  searchEl?.addEventListener('input', (e) => {
    clearTimeout(debounce);
    debounce = setTimeout(() => {
      searchQuery = e.target.value;
      applyFilters();
    }, 260);
  });
}



/* ── Combined filter application ──────────────────────────── */

/**
 * Apply collection filter + search query to allItems and re-render.
 * Call this whenever either activeCollection or searchQuery changes.
 */
function applyFilters() {
  /* 1. Collection filter — checks item.collections[].handle */
  const collectionItems = allItems.filter((item) =>
    itemMatchesCollection(item, activeCollection)
  );

  console.log('Collection filter', activeCollection);

  /* 2. Search filter on top of collection result */
  visibleItems = searchInventory(searchQuery, collectionItems);

  console.log('Filtered items', visibleItems.length);

  const isEmpty = visibleItems.length === 0;
  setEmpty(isEmpty);
  setError(false);

  if (!isEmpty) renderGrid(visibleItems);
  else          renderGrid([]);

  updateCount(visibleItems.length);
}

/* ── Init ──────────────────────────────────────────────────── */

async function init() {
  setError(false);
  setEmpty(false);
  setLoading(true);

  /* Reset filter + search state */
  activeCollection = 'all';
  searchQuery      = '';

  const searchEl = document.getElementById('inv-search');
  if (searchEl) searchEl.value = '';

  /* Reset chip UI to "All" */
  document.querySelectorAll('.inv-filter-chip').forEach((c) => {
    const btn = /** @type {HTMLButtonElement} */ (c);
    const isAll = btn.dataset.collection === 'all';
    c.classList.toggle('inv-filter-chip--active', isAll);
    c.setAttribute('aria-pressed', isAll ? 'true' : 'false');
  });

  try {
    allItems     = await loadInventory();
    visibleItems = allItems;
    console.debug('[Inventory] loaded', allItems.length, 'items');

    setLoading(false);

    if (allItems.length === 0) {
      setEmpty(true);
      updateCount(0);
      return;
    }

    renderGrid(allItems);
    updateCount(allItems.length);

  } catch (err) {
    console.error('[Inventory] init failed:', err);
    setLoading(false);
    setError(true);
    showToast('Could not load inventory.', 'error', 5000);
  }
}

/* ── Auth event listeners ──────────────────────────────────── */

document.addEventListener('inv:auth-success', (e) => {
  console.debug('[Inventory] inv:auth-success received — email:', e.detail?.email);
  bindEvents();
  init();
});

document.addEventListener('inv:auth-clear', () => {
  /* Reset UI when user logs out */
  allItems         = [];
  visibleItems     = [];
  activeCollection = 'all';
  searchQuery      = '';

  const grid    = document.getElementById('inv-grid');
  const loading = document.getElementById('inv-loading');
  if (grid)    { grid.innerHTML = ''; grid.hidden = false; }
  if (loading) loading.hidden = true;
  setEmpty(false);
  setError(false);

  const countEl = document.getElementById('inv-count');
  if (countEl) countEl.textContent = '';
});

/* ── Utility ───────────────────────────────────────────────── */

/**
 * Escape a value for safe insertion into HTML attributes and text.
 * @param {string} str
 * @returns {string}
 */
function esc(str) {
  if (typeof str !== 'string') return '';
  return str
    .replace(/&/g,  '&amp;')
    .replace(/</g,  '&lt;')
    .replace(/>/g,  '&gt;')
    .replace(/"/g,  '&quot;')
    .replace(/'/g,  '&#x27;');
}
